import type { AnyObject } from '@framework/validation';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  useForm,
  UseFormReturn,
  ValidationMode,
} from 'react-hook-form';
import type { InferType, ObjectSchema } from 'yup';
import { createYupAugmentedResolver } from '../resolver/createYupAugmentedResolver';
import { FormAugmentedContextProvider } from './augmented/FormAugmentedContext';
import { FormCoordinatorRegister } from './coordinator/FormCoordinatorRegister';
import { Form, IFormProps } from './Form';
import { FormPreventNavigation } from './prevent/FormPreventNavigation';

export interface IFormHandlerProps<
  T extends AnyObject,
  TSchema extends ObjectSchema<T> = ObjectSchema<T>,
> extends IFormProps {
  schema: TSchema;
  initialValues?: any;
  onSubmit: (
    data: InferType<TSchema>,
    event?: React.BaseSyntheticEvent,
    methods?: UseFormReturn<any, any, undefined>,
  ) => void | Promise<void>;
  /**
   * Gestisce l'errore quando il submit non è valido (ci sono degli errori)
   */
  onInvalid?: SubmitErrorHandler<InferType<TSchema>>;
  children: React.ReactNode;
  validationContext?: any;
  mode?: keyof ValidationMode;
  disableReinitialize?: boolean;
  /**
   * Elenco di pattern di pathname per cui la navigazione è sempre consentita
   * senza mostrare l'avviso di "modifiche non salvate".
   *
   * Quando l'utente prova a lasciare il form, se il pathname dell'URL di
   * destinazione corrisponde ad almeno una di queste espressioni regolari,
   * la navigazione prosegue senza attivare il prompt di conferma.
   *
   * N.B.: il match viene effettuato solo sul `location.pathname`, non su
   * query string (`search`) o hash (`hash`).
   */
  allowedUrls?: RegExp[];
}

export function FormHandler<
  T extends AnyObject,
  TSchema extends ObjectSchema<T>,
>(props: IFormHandlerProps<T, TSchema>) {
  const {
    schema,
    onSubmit,
    onInvalid,
    onBlur,
    initialValues,
    hideVisualFeedback,
    children,
    validationContext,
    mode,
    disableReinitialize,
    allowedUrls,
    ...formProps
  } = props;

  const resolver = useMemo(() => createYupAugmentedResolver(schema), [schema]);

  const methods = useForm({
    defaultValues: initialValues,
    // Prima dell'invio (submit)
    mode: mode ?? 'onBlur',
    // Dopo l'invio (submit)
    reValidateMode: 'onChange',
    // criteriaMode: 'all',
    shouldUnregister: false,
    resolver,
    context: validationContext,
  });

  useEffect(() => {
    if (disableReinitialize) return;
    methods.reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleReset = useCallback(
    () => methods.reset(initialValues),
    [initialValues, methods],
  );

  /**
   * Gestisce l'errore quando il submit non è valido (ci sono degli errori di
   * validazione). Se `showVisualFeedback` è true, fa lo scroll verso l'alto
   * della pagina.
   */
  const handleInvalid: SubmitErrorHandler<InferType<ObjectSchema<T>>> =
    useCallback(
      (errors, event) => {
        onInvalid?.(errors, event);
        if (!hideVisualFeedback) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      },
      [hideVisualFeedback, onInvalid],
    );

  // Passo anche i methods all'onSubmit dando la possibilità di chiamarli anche
  // durante l'invio del form (e.g. per resettare il form.)
  const handleOnSubmit = useCallback(
    (data: any, e: any) => onSubmit(data, e, methods),
    [methods, onSubmit],
  );

  return (
    <FormProvider {...methods}>
      <FormCoordinatorRegister id={props.id} methods={methods} />
      <FormAugmentedContextProvider
        schema={schema}
        validationContext={validationContext}
      >
        <Form
          onSubmit={handleOnSubmit}
          onInvalid={handleInvalid}
          onReset={handleReset}
          onBlur={onBlur}
          {...formProps}
        >
          {!hideVisualFeedback && (
            <FormPreventNavigation allowedUrls={allowedUrls} />
          )}
          {children}
        </Form>
      </FormAugmentedContextProvider>
    </FormProvider>
  );
}
